import React, { useContext, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../../../../../types/translation/TranslationKey';
import { ProductTransaction, ProductTransactionType } from '../../../../../../../../types/productTransaction';
import { Grid } from '@mui/material';
import DropdownSelect from '../../../../../../Common/DropdownSelect';
import { OrderParentType } from '../../../../../../../../types/order';
import { testIds } from '../../../../../../../../util/identifiers/identifiers.util';
import { t, translate } from '../../../../../../../../types/translation/Translator';
import { OrderContext } from '../../../../../../../../context/OrderContext';
import { ProductContext } from '../../../../../../../../context/ProductContext';
import { Container } from '../../../../../../../../types/container';
import { ContainerContext } from '../../../../../../../../context/ContainerContext';
import { CompanyContext } from '../../../../../../../../context/CompanyContext';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';
import CreateOrderCreateContainerModal from '../../Modals/CreateOrderCreateContainerModal';
import { IconButton } from '../../../../../../../icons';
import { IconType } from '../../../../../../../icons/Icon';
import { isValidSourceContainer } from '../../../../../../../../util/containers.util';
import { UnitOfMeasure } from '../../../../../../../../types/uom';
import { HandlingUnitContext } from '../../../../../../../../context/HandlingUnitContext';
import { System } from '../../../../../../../../types/system';
import { ProductMasterData } from '../../../../../../../../types/product_master_data';

interface ProductTransactionSourceContainerProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  disabled?: boolean;
}

export default function ProductTransactionSourceContainer({
  transaction,
  setTransaction,
  productMasterData,
  container,
  disabled = false,
}: ProductTransactionSourceContainerProps) {
  if (!productMasterData) return <ContainerTransactionSourceContainer container={container} />;

  const { currentCompany } = useContext(CompanyContext);
  const { orders } = useContext(OrderContext);
  const { containers } = useContext(ContainerContext);
  const { products } = useContext(ProductContext);
  const { handlingUnits } = useContext(HandlingUnitContext);

  const [openCreateContainerModal, setOpenCreateContainerModal] = useState(false);

  const showContainer = useMemo(() => currentCompany.settings.featureToggles.containers.containers, [currentCompany]);

  const selectedHandlingUnit = useMemo(() => {
    return handlingUnits.get(transaction.product.handlingUnitId || '');
  }, [handlingUnits, transaction.product.handlingUnitId]);

  const sourceProductMasterDataId = useMemo(() => {
    return selectedHandlingUnit?.parentId || productMasterData.id;
  }, [selectedHandlingUnit, productMasterData.id]);

  const containersForProduct = useMemo(() => {
    const quantityByContainer = new Map<string, number>();
    [...(products.get(sourceProductMasterDataId)?.values() || [])].forEach(p => {
      quantityByContainer.set(p.containerId || p.binId, parseFloat(p.unitQuantity || '0') || 0);
    });

    if (!selectedHandlingUnit) return quantityByContainer;

    const multiplier = selectedHandlingUnit.quantity;
    const result = new Map<string, number>();
    for (const [binId, quantity] of quantityByContainer.entries()) {
      result.set(binId, quantity * multiplier);
    }
    return result;
  }, [products, productMasterData, selectedHandlingUnit, sourceProductMasterDataId]);

  if (transaction.type === ProductTransactionType.inbound || !showContainer) return null;

  const isDisabled =
    transaction.statusId !== System.ProductTransactionStatus.createdId ||
    disabled ||
    (orders.get(transaction.parentId || '')?.parentType === OrderParentType.task &&
      transaction.type === ProductTransactionType.outbound) ||
    !!container ||
    !!transaction.containerTransactionId ||
    productMasterData.serialManaged;

  const validContainers = [...containers.values()]
    .filter(c =>
      isValidSourceContainer({
        container: c,
        transaction,
        products: [...(products.get(sourceProductMasterDataId)?.values() || [])].flat() || [],
        productMasterData,
      }),
    )
    .sort((a, b) => (containersForProduct.get(b.id) || 0) - (containersForProduct.get(a.id) || 0));

  return (
    <>
      <CreateOrderCreateContainerModal
        open={openCreateContainerModal}
        setOpen={setOpenCreateContainerModal}
        onCreated={c => setTransaction(transaction => transaction.withFromContainerId(c?.id))}
      />
      <Grid item xs={6}>
        <div className='flex h-full items-center gap-1'>
          <div className='flex-grow'>
            <DropdownSearchSelect
              disabled={disabled || isDisabled}
              placeholder={t(TranslationKey.sourceContainer).singular.label}
              label={t(TranslationKey.sourceContainer).singular.label}
              items={validContainers}
              value={containers.get(transaction.product.fromContainerId || '') || transaction.metadata?.fromContainer}
              onSelect={container => {
                setTransaction(transaction => transaction.withFromContainerId(container?.id));
              }}
              textBuilder={item => `${item?.identifier}`}
              itemBuilder={item => (
                <Grid container>
                  <Grid item flexGrow={1}>
                    <p>{item?.identifier}</p>
                  </Grid>
                  <Grid item my={'auto'}>
                    <p className='text-sm text-gray-400'>{`${UnitOfMeasure.toQuantityWithSuffix(
                      productMasterData.unitOfMeasure,
                      (containersForProduct.get(item?.id || '') || 0).toString(),
                    )}`}</p>
                  </Grid>
                </Grid>
              )}
            />
          </div>
          {!transaction.product.toContainerId && !disabled && !isDisabled ? (
            <div className='flex h-full items-center pt-6'>
              <IconButton
                className='border border-gray-default'
                type={IconType.plus}
                onClick={() => setOpenCreateContainerModal(true)}
                text={translate({ key: TranslationKey.createContainer })}
              />
            </div>
          ) : null}
        </div>
      </Grid>
    </>
  );
}

interface ContainerTransactionSourceContainerProps {
  container: Container | undefined;
}

function ContainerTransactionSourceContainer({ container }: ContainerTransactionSourceContainerProps) {
  if (!container) return null;

  const { containers } = useContext(ContainerContext);

  return (
    <Grid item xs={6}>
      <DropdownSelect
        testId={testIds.container}
        disabled
        placeholder={t(TranslationKey.sourceContainer).singular.label}
        label={t(TranslationKey.sourceContainer).singular.label}
        values={[]}
        selectedValue={containers.get(container.id) || null}
        onChange={() => {}}
        toText={item => item.identifier}
      />
    </Grid>
  );
}
